import React, { useEffect, useRef } from "react";
import useScreenSize from "../use-screensize";

export default function PieChartDashboard({
  data = [],
  showLegend = false,
  width = "100%",
  height = "31.25em",
  onSubjectClick,
}) {
  const chartRef = useRef(null);
  const screenSize = useScreenSize();

  useEffect(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return;

    const emToPx = (em) => {
      const base = chartRef.current
        ? parseFloat(getComputedStyle(chartRef.current).fontSize)
        : parseFloat(getComputedStyle(document.documentElement).fontSize);
      return em * base;
    };

    const SIZES = {
      innerRadiusPercent: 30,
      labelFontSizeEm: 0.875,
      labelMaxWidthEm: 3.75,
      labelOffsetEm: 2.25,
      strokeWidthEm: 0.425,
      cornerRadiusEm: 0.825,
      legendFontSizeEm: 0.875,
      hideBelowPercent: 10,
    };

    let chart;
    let series;

    const globalWin = typeof window !== "undefined" ? window : {};
    const { am4core, am4charts, am4themes_animated } = globalWin;

    if (!am4core || !am4charts || !chartRef.current) return;

    am4core.ready(function () {
      if (am4themes_animated) am4core.useTheme(am4themes_animated);

      chart = am4core.create(chartRef.current, am4charts.PieChart);
      if (chart.logo) chart.logo.disabled = true;

      // ✅ include id and color from your data
      chart.data = data.map((d) => ({
        subject: d.label,
        percentage: d.value,
        id: d.id,
        color: d.color, // hex or css color string is fine
      }));

      chart.innerRadius = am4core.percent(SIZES.innerRadiusPercent);

      series = chart.series.push(new am4charts.PieSeries());
      series.dataFields.value = "percentage";
      series.dataFields.category = "subject";

      // ✅ bind slice color to the "color" field
      series.slices.template.propertyFields.fill = "color";

      series.slices.template.cursorOverStyle = [
        { property: "cursor", value: "pointer" },
      ];

      series.slices.template.stroke = am4core.color("#ffffff");
      series.slices.template.strokeWidth = emToPx(SIZES.strokeWidthEm);
      series.slices.template.strokeOpacity = 1;

      const cornerPx = emToPx(SIZES.cornerRadiusEm);
      series.slices.template.cornerRadius = cornerPx;
      series.slices.template.innerCornerRadius = cornerPx;

      // Labels inside; no ticks
      series.alignLabels = false;
      series.ticks.template.disabled = true;

      // Percentage label inside slices
      series.labels.template.text = "{value.percent.formatNumber('#.0')}%";
      series.labels.template.fill = am4core.color("#ffffff");
      series.labels.template.fontWeight = "bold";
      series.labels.template.nonScaling = true;
      series.labels.template.bent = false;

      series.labels.template.fontSize = emToPx(SIZES.labelFontSizeEm);
      series.labels.template.maxWidth = emToPx(SIZES.labelMaxWidthEm);
      series.labels.template.truncate = true;
      series.labels.template.radius = -emToPx(SIZES.labelOffsetEm);
      series.labels.template.horizontalCenter = "middle";
      series.labels.template.verticalCenter = "middle";

      series.labels.template.adapter.add("text", (text, target) => {
        try {
          const di = target && target.dataItem;
          const pct =
            di && di.values && di.values.value && di.values.value.percent;
          if (typeof pct === "number" && pct < SIZES.hideBelowPercent) {
            return "";
          }
        } catch (e) {}
        return text;
      });

      // ✅ navigate on slice click
      series.slices.template.events.on("hit", (ev) => {
        try {
          const id = ev?.target?.dataItem?.dataContext?.id;
          if (id) {
            onSubjectClick(id);
          }
        } catch (e) {
          // no-op
        }
      });

      // Legend (optional), sized in em
      if (showLegend) {
        chart.legend = new am4charts.Legend();
        const legendPx = emToPx(SIZES.legendFontSizeEm);
        chart.legend.labels.template.fontSize = legendPx;
        chart.legend.markers.template.width = legendPx;
        chart.legend.markers.template.height = legendPx;
      } else {
        chart.legend = null;
      }
    });

    return () => {
      if (chart) chart.dispose();
    };
  }, [data, showLegend, screenSize.width]);

  return (
    <div
      ref={chartRef}
      style={{
        width,
        height,
      }}
    />
  );
}
